import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { FieldArray, Form, Formik } from 'formik';
import { createLoadHoc } from '../../helpers/hocs/createLoadHoc';
import { units } from '../../helpers/styles/units';
import { PageTitle } from '../../components/PageTitle';
import { FontWeight } from '../../constants/Styles/fontWeight';
import { useTypeSelector } from '../../store';
import { SubmitButtons } from '../../components/StyledComponents';
import { typography } from '../../helpers/styles/typography';
import { colors } from '../../constants/Colors';
import { Input } from '../../components/Input';
import { TypesIcon } from '../../types/TypesIcon';
import { addNotification } from '../../actions/notifications';
import { TypesSnackbars } from '../../types/TypesSnackbars';
import { isEqualObject } from '../../helpers/isEqual';
import { Switch } from '../../components/sw';
import {
    selectCertificates,
    selectGetCertificatesLoader,
} from '../../selectors/certificates';
import {
    CertificatesItem,
    SetCertificatesItem,
} from '../../types/Endpoints/certificates/CertificatesContracts';
import { setCertificates } from '../../actions/certificates/thunks';
import { Button } from '../../components/Button';
import { TypesButton } from '../../constants/ButtonTypes';
import { SizesButton } from '../../constants/SizeButton';
import { Icon } from '../../components/Icon';
import { addModal } from '../../actions/modal';
import { getId } from '../../helpers/generateId';
import { TypesModal } from '../../types/TypesModalComponent';

const EMPTY_CERTIFICATE: SetCertificatesItem = {
    sum: 0,
    guid: '',
    isActive: true,
};

const StyledRootWrapper = styled.div`
  position: relative;
  padding-left: ${units(12)};
`;
const StyledForm = styled(Form)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${units(40)};
`;
const StyledPageTitle = styled(PageTitle)`
  margin-bottom: ${units(20)};
`;
const StyledMainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 75%;
  gap: ${units(20)};
  padding-bottom: ${units(70)};
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: ${units(334)};
  padding-top: ${units(9)};
`;

const StyledInput = styled(Input)`
  width: 100%;

  font-weight: ${FontWeight.REGULAR};
  letter-spacing: 0.8px;

  margin-bottom: 0px;

  background-color: ${colors.light};

  ${typography(10)};

  & .input-title {
    color: ${colors.grey20};
  }
`;

const StyledSumWrapper = styled.div`
  width: ${units(235)};
`;

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(12)};
`;

const StyledFlexDiv = styled.div`
  display: flex;
  align-items: center;
  
  gap: ${units(16)};
`;
const SubmitButtonsWrapper = styled.div`
  margin-left: -${units(12)};
`;

const StyledButton = styled(Button)`
  width: fit-content;
`;

const DeleteButton = styled(StyledButton)`
  margin-top: ${units(4)};
`;

const AddButton = styled(StyledButton)`
  display: flex;
  align-items: center;
  padding: 0;
  gap: ${units(8)};

  font-weight: ${FontWeight.BOLD};
  border: none;

  &:hover {
    border: none;
  }
`;

const CertificatesContent = () => {
    const dispatch = useDispatch();
    const certificates = useTypeSelector(selectCertificates);

    const handleSubmit = (values: { certificates: CertificatesItem[] }) => {
        const data: SetCertificatesItem[] = values.certificates.map(item => ({
            sum: +item.sum,
            guid: item.guid,
            isActive: item.isActive,
        }));

        dispatch(setCertificates(data));
    };

    const initialValue: { certificates: CertificatesItem[] } = {
        certificates: certificates || [],
    };

    const handleResetForm = (clb: () => void, values: { certificates: CertificatesItem[] }) => {
        clb();
        const equal = isEqualObject(initialValue, values);

        if (!equal) {
            dispatch(
                addNotification({
                    type: TypesSnackbars.ERROR,
                    message: {
                        title: 'Изменения отменены',
                        description: '',
                    },
                    icon: TypesIcon.SNACK_ERROR,
                }),
            );
        }
    };

    const handleDelete = (remove: (index: number) => void, index: number) => {
        dispatch(
            addModal({
                id: getId()(),
                type: TypesModal.CONFIRM,
                message: {
                    description: 'Вы действительно хотите удалить сертификат?',
                },
                onSuccessMessage: 'Удалить',
                onSuccess: () => remove(index),
                withCloseIcon: true,
            }),
        );
    };

    return (
        <StyledRootWrapper>
            <StyledWrapper>
                <StyledPageTitle>Сертификаты</StyledPageTitle>
                <Formik
                    initialValues={initialValue}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {props => (
                        <StyledForm>
                            <FieldArray name="certificates">
                                {({ push, remove }) => (
                                    <StyledMainWrapper>
                                        {props.values.certificates?.map(
                                            (_certificate, index) => (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <StyledDiv key={`certificate-${index}`}>
                                                    <StyledFlexDiv>
                                                        <StyledSumWrapper>
                                                            <StyledInput
                                                                name={`certificates[${index}].sum`}
                                                                onChange={props.handleChange}
                                                                value={props.values?.certificates[index]?.sum}
                                                                title={`Номинал ${index + 1}`}
                                                                placeholder={`Номинал ${index + 1}`}
                                                                typeInput="text"
                                                                maxLength={255}
                                                            />
                                                        </StyledSumWrapper>

                                                        <Switch
                                                            name={`certificates[${index}].isActive`}
                                                            value={
                                                                props.values?.certificates[index]?.isActive
                                                            }
                                                            onChange={() => props.setFieldValue(
                                                                `certificates[${index}].isActive`,
                                                                !props.values?.certificates[index]?.isActive,
                                                            )}
                                                            type="checkbox"
                                                            title
                                                            inactiveTitle="Неактивный"
                                                            activeTitle="Активный"
                                                        />
                                                    </StyledFlexDiv>

                                                    <StyledInput
                                                        name={`certificates[${index}].guid`}
                                                        onChange={props.handleChange}
                                                        value={props.values?.certificates[index]?.guid}
                                                        title={`ID 1C (Номинал ${index + 1})`}
                                                        placeholder={`ID 1C (Номинал ${index + 1})`}
                                                        typeInput="text"
                                                        maxLength={255}
                                                    />

                                                    <DeleteButton
                                                        typeButton={TypesButton.DELETE}
                                                        size={SizesButton.M}
                                                        onClick={() => handleDelete(remove, index)}
                                                    >
                                                        <Icon
                                                            type={TypesIcon.TRASH_BIG}
                                                            width="24px"
                                                            height="24px"
                                                            viewBox="0 0 24 24"
                                                        />
                                                        Удалить
                                                    </DeleteButton>
                                                </StyledDiv>
                                            ),
                                        )}

                                        <AddButton
                                            typeButton={TypesButton.SECONDARY}
                                            size={SizesButton.M}
                                            onClick={() => push({ ...EMPTY_CERTIFICATE })}
                                        >
                                            <Icon
                                                type={TypesIcon.PLUS_BIG}
                                                width="24px"
                                                height="24px"
                                                viewBox="0 0 24 24"
                                                color="#000000"
                                            />
                                            {' '}
                                            Добавить сертификат
                                        </AddButton>
                                    </StyledMainWrapper>
                                )}
                            </FieldArray>
                            <SubmitButtonsWrapper>
                                <SubmitButtons
                                    reset={() => handleResetForm(props.resetForm, props.values)}
                                    submit={props.submitForm}
                                />
                            </SubmitButtonsWrapper>
                        </StyledForm>
                    )}
                </Formik>
            </StyledWrapper>
        </StyledRootWrapper>
    );
};

const loader = createLoadHoc({
    selectLoader: selectGetCertificatesLoader,
});

export const CertificatesWithLoader = loader(CertificatesContent);
